import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, TrendingUp, Users, Target, ArrowRight, BarChart3, Calendar, Filter, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { InfoTip, RATE_FORMULAS } from "@/components/InfoTip";
import { HeadlineExperimentConfig } from "@/components/experiments/HeadlineExperimentConfig";
import { HeadlineExperimentResults } from "@/components/experiments/HeadlineExperimentResults";
import { PaymentReconciliation } from "@/components/experiments/PaymentReconciliation";
import { ChannelTable } from "@/components/ChannelTable";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Step colors for the funnel visualization
const STEP_COLORS = [
  "bg-blue-500",    // Service Type
  "bg-blue-400",    // Window Quantity
  "bg-cyan-500",    // Window Breakdown
  "bg-cyan-400",    // Slider Quantity
  "bg-teal-500",    // Slider Breakdown
  "bg-teal-400",    // Contact Info
  "bg-emerald-500", // Address
  "bg-green-500",   // Estimate Shown
  "bg-green-400",   // Booking
  "bg-green-600"    // Booked
];

export default function FunnelAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/funnel/analytics?days=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch funnel analytics:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate('/settings');
      } else {
        toast.error("Failed to load analytics");
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate the width percentage for funnel bars
  const getBarWidth = (views, maxViews) => {
    if (maxViews === 0) return 0;
    return Math.max(5, (views / maxViews) * 100); // Min 5% for visibility
  };

  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  const maxViews = analytics?.step_metrics?.[0]?.views || 1;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/settings')}
              className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-600" />
            </button>
            <div>
              <h1 className="font-barlow text-2xl font-bold text-zinc-950 uppercase">Estimate Funnel</h1>
              <p className="text-sm text-zinc-500">Track where visitors drop off in the estimate flow</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Date Range Filter */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <span className="text-sm text-zinc-600">Date Range:</span>
          </div>
          <div className="flex gap-2">
            {[7, 14, 30, 90, 180, 365].map((days) => (
              <button
                key={days}
                onClick={() => setDateRange(days)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === days 
                    ? 'bg-[#207024] text-white' 
                    : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                }`}
                data-testid={`date-range-${days}`}
              >
                {days}d
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="total-sessions-card">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-zinc-500 uppercase tracking-wide">Total Visitors</span>
            </div>
            <p className="text-2xl font-bold text-zinc-900">{analytics?.summary?.total_sessions || 0}</p>
          </div>

          <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="estimates-shown-card">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-green-500" />
              <span className="text-xs text-zinc-500 uppercase tracking-wide">Saw Estimate</span>
              <InfoTip text={RATE_FORMULAS.estimate_view_rate} testid="tip-saw-estimate" />
            </div>
            <p className="text-2xl font-bold text-zinc-900">{analytics?.summary?.reached_estimate || 0}</p>
            <p className="text-xs text-zinc-500">{analytics?.summary?.estimate_view_rate || 0}% of visitors</p>
          </div>

          <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="bookings-card">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-zinc-500 uppercase tracking-wide">Online Bookings</span>
              <InfoTip text={RATE_FORMULAS.online_bookings} testid="tip-online-bookings" />
            </div>
            <p className="text-2xl font-bold text-zinc-900">{analytics?.summary?.completed_bookings || 0}</p>
            <p className="text-xs text-zinc-500">{analytics?.metrics?.unique_estimates || 0} unique estimates</p>
          </div>

          <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="paid-revenue-card">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-zinc-500 uppercase tracking-wide">Paid Revenue</span>
            </div>
            <p className="text-2xl font-bold text-zinc-900">${(analytics?.metrics?.paid_revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            <p className="text-xs text-zinc-500">${(analytics?.metrics?.booked_revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} booked</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="conversion-rates">
          {[
            ["quote_book_rate", "Quote Book Rate", analytics?.metrics?.booked_estimates, analytics?.metrics?.unique_estimates],
            ["online_self_book_rate", "Online Self-Book Rate", analytics?.metrics?.online_self_booked, analytics?.metrics?.funnel_estimates_shown],
            ["close_rate_paid", "Close Rate (paid)", analytics?.metrics?.paid_estimates, analytics?.metrics?.unique_estimates],
          ].map(([key, label, num, den]) => (
            <div key={key} className="bg-white rounded-xl border border-zinc-200 p-4" data-testid={`rate-card-${key}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs text-zinc-500 uppercase tracking-wide">{label}</span>
                <InfoTip text={RATE_FORMULAS[key]} testid={`tip-${key}`} />
              </div>
              <p className="text-2xl font-bold text-zinc-900" data-testid={`rate-${key}`}>{analytics?.metrics?.[key] || 0}%</p>
              <p className="text-[11px] text-zinc-400">{num || 0} of {den || 0} unique estimates</p>
            </div>
          ))}
        </div>
        <PaymentReconciliation data={analytics?.paid_reconciliation} />
        <ChannelTable rows={analytics?.by_channel} />

        {/* Funnel Visualization */}
        <div className="bg-white rounded-xl border border-zinc-200 p-6" data-testid="funnel-chart">
          <h2 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#207024]" />
            Conversion Funnel
          </h2>

          <div className="space-y-3">
            {analytics?.step_metrics?.map((step, index) => {
              const nextStep = analytics.step_metrics[index + 1];
              const dropOff = nextStep ? step.views - nextStep.views : 0;
              const dropOffPercent = step.views > 0 ? ((dropOff / step.views) * 100).toFixed(1) : 0;
              
              return (
                <div key={step.step} className="relative" data-testid={`funnel-step-${step.step}`}>
                  <div className="flex items-center gap-4">
                    {/* Step label */}
                    <div className="w-32 text-right">
                      <span className="text-sm font-medium text-zinc-700">{step.name}</span>
                    </div>
                    
                    {/* Bar */}
                    <div className="flex-1 relative h-10">
                      <div 
                        className={`h-full ${STEP_COLORS[index]} rounded-lg transition-all duration-500 flex items-center justify-end pr-3`}
                        style={{ width: `${getBarWidth(step.views, maxViews)}%` }}
                      >
                        <span className="text-white text-sm font-semibold">{step.views}</span>
                      </div>
                    </div>
                    
                    {/* Drop-off indicator */}
                    <div className="w-24 text-left">
                      {dropOff > 0 && nextStep && (
                        <span className="text-xs text-red-500 font-medium">
                          -{dropOff} ({dropOffPercent}%)
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Arrow between steps */}
                  {index < analytics.step_metrics.length - 1 && (
                    <div className="flex items-center justify-center py-1 ml-32">
                      <ArrowRight className="w-4 h-4 text-zinc-300 rotate-90" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Funnel Version A/B/C Comparison (V1 vs V2 vs V3) */}
        {analytics?.version_breakdown?.length > 0 && (
          <div className="bg-white rounded-xl border border-zinc-200 p-6" data-testid="version-breakdown">
            <h2 className="font-semibold text-zinc-900 mb-1 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Funnel Version: V1 vs V2 vs V3
            </h2>
            <p className="text-xs text-zinc-500 mb-4">
              Compare each funnel version side-by-side. V1 = original, V2 = simplified condition picker,
              V3 = Whole-Home Screen Check. Historical data is preserved when switching versions.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {analytics.version_breakdown.map((row) => {
                const isV3 = row.version === "v3";
                const isV2 = row.version === "v2";
                const accent = isV3
                  ? "border-emerald-300 bg-emerald-50"
                  : isV2
                    ? "border-purple-200 bg-purple-50"
                    : "border-zinc-200 bg-zinc-50";
                const dot = isV3 ? "bg-emerald-500" : isV2 ? "bg-purple-500" : "bg-zinc-400";
                const label = isV3 ? "Funnel V3 (Screen Check)" : isV2 ? "Funnel V2 (Simplified)" : "Funnel V1 (Original)";
                const winnerColor = isV3 ? "text-emerald-700" : isV2 ? "text-purple-700" : "text-[#207024]";
                return (
                  <div
                    key={row.version}
                    className={`rounded-xl border-2 p-4 ${accent}`}
                    data-testid={`version-card-${row.version}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
                        <span className="font-semibold text-zinc-900 uppercase text-xs tracking-wide">
                          {label}
                        </span>
                      </div>
                      <span className="text-xs text-zinc-500">{row.sessions} visits</span>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Saw Estimate</p>
                        <p className="text-lg font-bold text-zinc-900">{row.estimate_view_rate}%</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Quote Book <InfoTip text={RATE_FORMULAS.quote_book_rate} /></p>
                        <p className={`text-lg font-bold ${winnerColor}`} data-testid={`version-quote-to-book-${row.version}`}>{row.quote_book_rate || 0}%</p>
                        <p className="text-[10px] text-zinc-400">{row.booked_estimates || 0}/{row.estimates || 0}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Close (paid) <InfoTip text={RATE_FORMULAS.close_rate_paid} /></p>
                        <p className="text-lg font-bold text-zinc-900" data-testid={`version-close-rate-${row.version}`}>{row.close_rate_paid || 0}%</p>
                        <p className="text-[10px] text-zinc-400">{row.completed_jobs || 0}/{row.estimates || 0}</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-2 text-center" data-testid={`version-revenue-${row.version}`}>
                      Online self-book {row.online_self_book_rate || 0}% · ${(row.booked_revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} booked · ${(row.paid_revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} paid
                    </p>

                    <div className="mt-3 pt-3 border-t border-white/70 grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Screens/Job</p>
                        <p className="text-sm font-bold text-zinc-900">
                          {row.avg_screens_completed || "—"}
                          {row.avg_screens_added > 0 && <span className="text-[10px] text-zinc-400 font-normal ml-1">+{row.avg_screens_added}</span>}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Avg Job Value</p>
                        <p className="text-sm font-bold text-zinc-900">
                          ${row.avg_completed_value ? row.avg_completed_value.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Rev / Lead</p>
                        <p className="text-sm font-bold text-zinc-900">
                          ${row.revenue_per_lead ? row.revenue_per_lead.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Winner callout */}
            {(() => {
              const rows = analytics.version_breakdown.filter((r) => r.sessions >= 5);
              if (rows.length < 2) {
                return (
                  <p className="text-xs text-zinc-400 italic mt-4">
                    Need at least 5 sessions per version for a meaningful comparison.
                  </p>
                );
              }
              const sorted = [...rows].sort((a, b) => (b.quote_book_rate || 0) - (a.quote_book_rate || 0));
              const winner = sorted[0];
              const runnerUp = sorted[1];
              const lift = ((winner.quote_book_rate || 0) - (runnerUp.quote_book_rate || 0)).toFixed(1);
              return (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <strong>{winner.version.toUpperCase()}</strong> is currently winning with a{" "}
                  <strong>{lift} pts</strong> higher Quote Book Rate vs {runnerUp.version.toUpperCase()}.
                </div>
              );
            })()}
          </div>
        )}

        {/* V3 Screen Check Performance table */}
        {analytics?.screen_check_breakdown?.length > 0 && (
          <div className="bg-white rounded-xl border border-zinc-200 p-6" data-testid="screen-check-breakdown">
            <h2 className="font-semibold text-zinc-900 mb-1 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              V3 Screen Check Performance
            </h2>
            <p className="text-xs text-zinc-500 mb-4">
              How each answer to "check the rest of your screens?" affected screens completed and revenue per job.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-zinc-500 uppercase tracking-wide border-b border-zinc-200">
                    <th className="py-2 pr-3">Selection</th>
                    <th className="py-2 pr-3 text-right">Customers</th>
                    <th className="py-2 pr-3 text-right">Share</th>
                    <th className="py-2 pr-3 text-right">Avg Quoted</th>
                    <th className="py-2 pr-3 text-right">Avg Completed</th>
                    <th className="py-2 pr-3 text-right">Avg Added</th>
                    <th className="py-2 pr-3 text-right">Avg Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.screen_check_breakdown.map((row, idx) => {
                    const label = row.choice === "whole_home_check"
                      ? "Whole-Home Check"
                      : row.choice === "quick_check"
                        ? "Quick Check"
                        : "No Thanks";
                    return (
                      <tr
                        key={`${row.version}-${row.choice}`}
                        className={idx % 2 === 0 ? "bg-zinc-50" : ""}
                        data-testid={`screen-check-row-${row.choice}`}
                      >
                        <td className="py-2 pr-3 font-medium text-zinc-800">
                          {label}
                          <span className="ml-2 text-[10px] text-zinc-400 uppercase">{row.version}</span>
                        </td>
                        <td className="py-2 pr-3 text-right">{row.customers}</td>
                        <td className="py-2 pr-3 text-right">{row.share_percent}%</td>
                        <td className="py-2 pr-3 text-right">{row.avg_screens_quoted || "—"}</td>
                        <td className="py-2 pr-3 text-right">{row.avg_screens_completed || "—"}</td>
                        <td className="py-2 pr-3 text-right">
                          {row.avg_screens_added > 0 ? (
                            <span className="text-emerald-700 font-semibold">+{row.avg_screens_added}</span>
                          ) : row.avg_screens_added < 0 ? (
                            <span className="text-red-600">{row.avg_screens_added}</span>
                          ) : "—"}
                        </td>
                        <td className="py-2 pr-3 text-right font-semibold">
                          {row.avg_completed_value ? `$${row.avg_completed_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Traffic Sources */}
        {analytics?.source_breakdown?.length > 0 && (
          <div className="bg-white rounded-xl border border-zinc-200 p-6" data-testid="source-breakdown">
            <h2 className="font-semibold text-zinc-900 mb-4">Traffic Sources</h2>
            <div className="space-y-3">
              {analytics.source_breakdown.map((source) => (
                <div key={source.source} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      source.source === 'direct' ? 'bg-zinc-400' :
                      source.source === 'google' ? 'bg-blue-500' :
                      source.source === 'facebook' ? 'bg-indigo-500' :
                      'bg-emerald-500'
                    }`} />
                    <span className="text-sm font-medium text-zinc-700 capitalize">{source.source}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-zinc-500">{source.sessions} visits</span>
                    <span className="text-sm font-medium text-[#207024]">{source.completions} bookings</span>
                    <span className="text-xs text-zinc-400">
                      ({source.sessions > 0 ? ((source.completions / source.sessions) * 100).toFixed(1) : 0}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Insights */}
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-6" data-testid="insights-section">
          <h2 className="font-semibold text-amber-800 mb-3">Key Insights</h2>
          <ul className="space-y-2 text-sm text-amber-700">
            {analytics?.summary?.total_sessions === 0 ? (
              <li>• No data yet. Funnel events will appear as visitors use the instant estimate form.</li>
            ) : (
              <>
                {analytics?.step_metrics?.slice(0, -1).map((step, index) => {
                  const nextStep = analytics.step_metrics[index + 1];
                  if (!nextStep) return null;
                  const dropOffPercent = step.views > 0 ? ((step.views - nextStep.views) / step.views) * 100 : 0;
                  if (dropOffPercent > 30) {
                    return (
                      <li key={step.step}>
                        • <strong>{dropOffPercent.toFixed(0)}%</strong> of visitors drop off at "{step.name}" → Consider simplifying this step
                      </li>
                    );
                  }
                  return null;
                })}
                {analytics?.summary?.estimate_view_rate > 50 && (
                  <li>• Great job! Over half of visitors see their estimate.</li>
                )}
                {analytics?.summary?.booking_completion_rate < 30 && analytics?.summary?.reached_booking > 0 && (
                  <li>• Booking completion is low. Consider reducing friction in the scheduling step.</li>
                )}
              </>
            )}
          </ul>
        </div>

        <ExperimentsSection />
      </div>
    </div>
  );
}

function ExperimentsSection() {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-6" id="experiments" data-testid="experiments-section">
      <HeadlineExperimentConfig expKey="ie_headline_v1" onSaved={() => setRefreshKey((k) => k + 1)} />
      <HeadlineExperimentResults expKey="ie_headline_v1" refreshKey={refreshKey} />
    </div>
  );
}
